import clsx from 'clsx';
import React, { ReactNode } from 'react';
import classes from './Button.module.scss';

export type ButtonType = 'primary' | 'secondary' | 'tertiary' | 'text' | 'card';
export type ButtonColor = 'primary' | 'secondary' | 'accent';

interface ButtonStyleProps {
  buttonSize?: 'small' | 'normal' | 'large';
  color?: ButtonColor;
  buttonType?: ButtonType;
}

type Without<T, K> = Pick<T, Exclude<keyof T, K>>;
export type Props<T extends React.ElementType = 'button'> = {
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
  component?: T;
  iconStyle?: 'iconOnly'; // We can expand this to be 'left', 'right' etc when needed
  icon?: ReactNode;
  ref?: React.ForwardedRef<T>;
} & ButtonStyleProps &
  Without<React.ComponentPropsWithoutRef<T>, ButtonStyleProps>;

function Button<T extends React.ElementType = 'button'>({
  className,
  disabled,
  children,
  component,
  buttonSize = 'normal',
  color = 'primary',
  buttonType = 'secondary',
  iconStyle,
  icon,
  ref,
  ...rest
}: Props<T>) {
  const Root = (component || 'button') as React.ElementType;
  return (
    <Root
      type="button"
      className={clsx(
        classes.root,
        classes[`type-${buttonType}`],
        classes[`color-${color}`],
        classes[`size-${buttonSize}`],
        disabled ? classes.disabled : '',
        icon ? classes.icon : '',
        icon && iconStyle === 'iconOnly' ? classes.iconOnly : '',
        className,
      )}
      ref={ref}
      disabled={disabled}
      {...rest}
    >
      {icon && iconStyle == 'iconOnly' && icon}
      {icon && iconStyle !== 'iconOnly' && (
        <>
          {children}
          {icon}
        </>
      )}
      {!icon && children}
    </Root>
  );
}

export default Button;
